import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Mic, MicOff, Play, Pause, ArrowLeft, ChevronRight, 
  Volume2, Check, X, RotateCcw, Save, Loader2, Headphones
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LevelSkillTest = () => {
  const { level, type } = useParams();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Speaking states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Listening states
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const speechSynthRef = useRef(null);
  
  const levelNames = {
    'level_0': 'A0 - Absolute Beginner',
    'level_1': 'A1 - Beginner',
    'level_2': 'A2 - Elementary',
    'level_3': 'B1 - Intermediate',
    'level_4': 'B1 - Interview',
    'level_5': 'B2 - Upper Intermediate',
    'level_6': 'B2 - Interview',
    'level_7': 'C1 - Advanced',
    'level_8': 'C1 - Interview'
  };
  
  useEffect(() => {
    fetchQuestions();
  }, [level, type]);
  
  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_URL}/api/skill-tests/${type}/${level}`,
        { withCredentials: true }
      );
      setQuestions(response.data.questions);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Sorular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  // Speaking functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setSaved(false);
    } catch (err) {
      console.error('Mikrofon erişimi hatası:', err);
      alert('Mikrofon erişimi sağlanamadı. Lütfen izin verin.');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  const saveRecording = async () => {
    if (!audioBlob || !questions[currentIndex]) return;
    
    setSaving(true);
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        await axios.post(
          `${API_URL}/api/skill-tests/speaking/save-recording`,
          {
            level: level.toUpperCase().replace('LEVEL_', ''),
            question_id: questions[currentIndex].id,
            question_text: questions[currentIndex].question,
            audio_data: base64Audio,
            duration_seconds: 0
          },
          { withCredentials: true }
        );
        
        setSaved(true);
        setSaving(false);
      };
    } catch (err) {
      console.error('Kayıt hatası:', err);
      alert('Kayıt kaydedilemedi');
      setSaving(false);
    }
  };
  
  const resetRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setSaved(false);
  };
  
  // Audio playback function - works for both Speaking and Listening
  const playAudio = () => {
    // For speaking: read the question text
    // For listening: read the audio_text (the audio transcript)
    const textToSpeak = type === 'speaking' 
      ? questions[currentIndex]?.question 
      : questions[currentIndex]?.audio_text;
    
    if (!textToSpeak) return;
    
    if (speechSynthRef.current) {
      window.speechSynthesis.cancel();
    }
    
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsPlaying(true);
  };
  
  const selectAnswer = async (index) => {
    if (answerResult) return;
    
    setSelectedAnswer(index);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/skill-tests/listening/submit-answer`,
        {
          level: level.toUpperCase().replace('LEVEL_', ''),
          question_id: questions[currentIndex].id,
          selected_index: index
        },
        { withCredentials: true }
      );
      
      setAnswerResult(response.data);
    } catch (err) {
      console.error('Cevap gönderme hatası:', err);
    }
  };
  
  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setSaved(false);
    }
  };
  
  const prevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setSaved(false);
    }
  };
  
  const isSpeaking = type === 'speaking';
  const headerColor = isSpeaking ? 'from-rose-500 to-pink-600' : 'from-emerald-500 to-teal-600';
  const accentColor = isSpeaking ? 'rose' : 'emerald';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 border-${accentColor}-500`}></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className={`bg-gradient-to-r ${headerColor} pb-8 rounded-b-[2rem]`}>
          <div className="max-w-4xl mx-auto px-4 pt-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Geri</span>
            </button>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)}>Geri Dön</Button>
        </div>
      </div>
    );
  }
  
  const currentQuestion = questions[currentIndex];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${headerColor} pb-8 rounded-b-[2rem]`}>
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                {isSpeaking ? (
                  <Mic className="w-8 h-8 text-rose-500" />
                ) : (
                  <Headphones className="w-8 h-8 text-emerald-500" />
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isSpeaking ? 'Speaking Test' : 'Listening Test'}
                </h1>
                <p className="text-white/80 text-sm">{levelNames[level] || level}</p>
              </div>
            </div>
            <div className="text-white text-right">
              <p className="text-2xl font-bold">{currentIndex + 1} / {questions.length}</p>
              <p className="text-white/70 text-sm">Soru</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 w-full bg-white/30 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all"
              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            {isSpeaking ? (
              // Speaking UI
              <div className="text-center">
                {/* Listen to Question Button */}
                <div className="flex justify-center mb-4">
                  <button
                    onClick={playAudio}
                    disabled={isPlaying}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-md ${
                      isPlaying 
                        ? 'bg-rose-400 animate-pulse' 
                        : 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600'
                    }`}
                  >
                    {isPlaying ? (
                      <Volume2 className="w-7 h-7 text-white animate-bounce" />
                    ) : (
                      <Play className="w-7 h-7 text-white ml-1" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  {isPlaying ? '🔊 Soruyu dinliyorsunuz...' : '🎧 Soruyu dinlemek için tıklayın'}
                </p>
                
                <h2 className="text-xl font-bold mb-2 text-gray-800">{currentQuestion?.question}</h2>
                <p className="text-gray-500 mb-4">{currentQuestion?.turkish}</p>
                
                {currentQuestion?.tip && (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6 text-left">
                    <p className="text-sm text-rose-700">💡 <strong>Tip:</strong> {currentQuestion.tip}</p>
                  </div>
                )}
                
                {/* Recording Controls */}
                <div className="flex flex-col items-center gap-4">
                  {!audioUrl ? (
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      className={`w-24 h-24 rounded-full flex items-center justify-center transition-all shadow-lg ${
                        isRecording 
                          ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                          : 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600'
                      }`}
                    >
                      {isRecording ? (
                        <MicOff className="w-10 h-10 text-white" />
                      ) : (
                        <Mic className="w-10 h-10 text-white" />
                      )}
                    </button>
                  ) : (
                    <div className="space-y-4 w-full max-w-md">
                      <audio src={audioUrl} controls className="w-full" />
                      
                      <div className="flex gap-3 justify-center">
                        <Button variant="outline" onClick={resetRecording} className="flex-1">
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Tekrar Kaydet
                        </Button>
                        <Button 
                          onClick={saveRecording} 
                          disabled={saving || saved}
                          className={`flex-1 ${saved ? 'bg-green-500 hover:bg-green-600' : 'bg-gradient-to-r from-rose-500 to-pink-500'}`}
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : saved ? (
                            <Check className="w-4 h-4 mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          {saved ? 'Kaydedildi!' : 'Kaydet'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-500 mt-2">
                    {isRecording ? '🔴 Kaydediliyor... Durdurmak için tıklayın' : 
                     audioUrl ? '✅ Kaydınızı dinleyebilir ve kaydedebilirsiniz' :
                     '🎤 Kayıt başlatmak için mikrofona tıklayın'}
                  </p>
                </div>
              </div>
            ) : (
              // Listening UI
              <div>
                {/* Play Audio Button */}
                <div className="flex justify-center mb-6">
                  <button
                    onClick={playAudio}
                    disabled={isPlaying}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all shadow-lg ${
                      isPlaying 
                        ? 'bg-emerald-400 animate-pulse' 
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
                    }`}
                  >
                    {isPlaying ? (
                      <Volume2 className="w-10 h-10 text-white animate-bounce" />
                    ) : (
                      <Play className="w-10 h-10 text-white ml-1" />
                    )}
                  </button>
                </div>
                <p className="text-center text-sm text-gray-500 mb-6">
                  {isPlaying ? '🔊 Dinleniyor...' : '🎧 Sesi dinlemek için tıklayın'}
                </p>
                
                <h2 className="text-xl font-bold mb-6 text-center text-gray-800">{currentQuestion?.question}</h2>
                
                {/* Options */}
                <div className="space-y-3 max-w-md mx-auto">
                  {currentQuestion?.options?.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectAnswer(idx)}
                      disabled={answerResult !== null}
                      className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                        answerResult 
                          ? idx === answerResult.correct_index
                            ? 'bg-green-100 border-green-500'
                            : selectedAnswer === idx
                              ? 'bg-red-100 border-red-500'
                              : 'bg-gray-50 border-gray-200'
                          : selectedAnswer === idx
                            ? 'bg-emerald-100 border-emerald-500'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          answerResult && idx === answerResult.correct_index
                            ? 'bg-green-500 text-white'
                            : answerResult && selectedAnswer === idx
                              ? 'bg-red-500 text-white'
                              : 'bg-white border-2 border-gray-200'
                        }`}>
                          {String.fromCharCode(65 + idx)}
                        </span>
                        <span className="font-medium">{option}</span>
                        {answerResult && idx === answerResult.correct_index && (
                          <Check className="w-5 h-5 text-green-600 ml-auto" />
                        )}
                        {answerResult && selectedAnswer === idx && idx !== answerResult.correct_index && (
                          <X className="w-5 h-5 text-red-600 ml-auto" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                
                {answerResult && (
                  <div className={`mt-6 p-4 rounded-xl text-center ${
                    answerResult.is_correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {answerResult.is_correct ? '✅ Doğru!' : '❌ Yanlış!'} {answerResult.explanation}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={prevQuestion}
            disabled={currentIndex === 0}
            className="px-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Önceki
          </Button>
          
          {currentIndex < questions.length - 1 ? (
            <Button 
              onClick={nextQuestion}
              className={`px-6 bg-gradient-to-r ${headerColor}`}
            >
              Sonraki
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={() => navigate(-1)}
              className="px-6 bg-gradient-to-r from-green-500 to-emerald-500"
            >
              <Check className="w-4 h-4 mr-2" />
              Tamamla
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelSkillTest;
